import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AccountLink from '../../components/Common/AccountLink';
import './styles.css';

const TransactionResult = () => {
  const { transactionId } = useParams();
  const navigate = useNavigate();
  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchTransaction = async () => {
      if (!transactionId) {
        setError('Transaction ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);const response = await fetch('https://api.steemit.com', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            jsonrpc: '2.0',
            method: 'condenser_api.get_transaction',
            params: [transactionId],
            id: 1,
          }),
        });

        const data = await response.json();

        if (data.error) {
          throw new Error(data.error.message || 'Failed to fetch transaction');
        }

        if (!data.result) {
          throw new Error('Transaction not found');
        }setTransaction(data.result);
      } catch (err) {setError(err.message || 'Failed to fetch transaction details');
      } finally {
        setLoading(false);
      }
    };

    fetchTransaction();
  }, [transactionId]);

  // Search functionality
  const detectSearchType = (input) => {
    const trimmedInput = input.trim();

    // Check if it's a block number (only digits)
    if (/^\d+$/.test(trimmedInput)) {
      return { type: 'block', value: trimmedInput };
    }

    // Check if it's a transaction ID (hexadecimal string, typically 40 characters)
    if (/^[a-fA-F0-9]{40}$/.test(trimmedInput)) {
      return { type: 'transaction', value: trimmedInput };
    }

    // Check if it's an account name (alphanumeric with dots, dashes, typically 3-16 chars)
    if (/^[a-zA-Z0-9.-]{3,16}$/.test(trimmedInput)) {
      return { type: 'account', value: trimmedInput };
    }

    // Default to account if unclear
    return { type: 'account', value: trimmedInput };
  };

  const handleSearch = (e) => {
    e.preventDefault();

    if (!searchTerm.trim()) {
      return;
    }

    const searchResult = detectSearchType(searchTerm);

    switch (searchResult.type) {
      case 'block':
        navigate(`/blocks/${searchResult.value}`);
        break;
      case 'transaction':
        navigate(`/transactions/${searchResult.value}`);
        break;
      case 'account':
        navigate(`/accounts/${searchResult.value}`);
        break;
      default:
        navigate(`/accounts/${searchResult.value}`);
    }
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const getOperationIcon = (opType) => {
    const icons = {
      'transfer': '💸',
      'vote': '🗳️',
      'comment': '💬',
      'comment_options': '⚙️',
      'delete_comment': '🗑️',
      'custom_json': '🔧',
      'account_create': '👤',
      'account_update': '✏️',
      'witness_update': '🏛️',
      'account_witness_vote': '🗳️',
      'account_witness_proxy': '🔗',
      'pow': '⚡',
      'custom': '🔧',
      'report_over_production': '📊',
      'limit_order_create': '📈',
      'limit_order_cancel': '❌',
      'price_feed_publish': '💰',
      'convert': '🔄',
      'account_create_with_delegation': '👥',
      'withdraw_vesting': '📤',
      'set_withdraw_vesting_route': '🛣️',
      'claim_account': '🎫',
      'create_claimed_account': '✨',
      'request_account_recovery': '🆘',
      'recover_account': '🔒',
      'change_recovery_account': '🔑',
      'escrow_transfer': '🤝',
      'escrow_dispute': '⚖️',
      'escrow_release': '✅',
      'pow2': '⚡',
      'escrow_approve': '✓',
      'transfer_to_vesting': '💎',
      'delegate_vesting_shares': '🤝'
    };
    return icons[opType] || '📄';
  };

  const getOperationColor = (opType) => {
    const colors = {
      'transfer': '#10b981', // green
      'vote': '#3b82f6', // blue  
      'comment': '#8b5cf6', // purple
      'comment_options': '#6b7280', // gray
      'delete_comment': '#ef4444', // red
      'custom_json': '#f59e0b', // amber
      'account_create': '#06d6a0', // teal
      'account_update': '#8b5cf6', // purple
      'witness_update': '#f59e0b', // amber
      'account_witness_vote': '#3b82f6', // blue
      'account_witness_proxy': '#6366f1', // indigo
      'transfer_to_vesting': '#10b981', // green
      'delegate_vesting_shares': '#06d6a0', // teal
      'withdraw_vesting': '#f97316', // orange
      'limit_order_create': '#059669', // emerald
      'limit_order_cancel': '#dc2626', // red
      'price_feed_publish': '#d97706', // amber
      'convert': '#7c3aed', // violet
      'escrow_transfer': '#0891b2', // cyan
      'claim_account': '#16a34a', // green
      'create_claimed_account': '#22c55e' // green
    };
    return colors[opType] || '#06d6a0';
  };

  const formatAmount = (amount) => {
    if (!amount) return 'N/A';
    const parts = amount.split(' ');
    if (parts.length === 2) {
      const value = parseFloat(parts[0]);
      const currency = parts[1];
      return (
        <span className="amount-display">
          <span className="amount-value">{value.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</span>
          <span className="amount-currency">{currency}</span>
        </span>
      );
    }
    return amount;
  };

  const formatVoteWeight = (weight) => {
    const percentage = (weight / 100).toFixed(2);
    const isUpvote = weight > 0;
    const isDownvote = weight < 0;
    
    return (
      <span className={`vote-weight ${isUpvote ? 'upvote' : isDownvote ? 'downvote' : 'neutral'}`}>
        {isUpvote ? '👍' : isDownvote ? '👎' : '➖'} {Math.abs(percentage)}%
      </span>
    );
  };

  const renderOperationDetails = (operation) => {
    const [opType, opData] = operation;

    switch (opType) {
      case 'transfer':
        return (
          <div className="operation-details">
            <div className="operation-summary">
              <div className="summary-box transfer-summary">
                <span className="summary-icon">💸</span>
                <div className="summary-content">
                  <div className="summary-title">Transfer</div>
                  <div className="summary-description">
                    <AccountLink account={opData.from} /> → <AccountLink account={opData.to} />
                  </div>
                </div>
                <div className="summary-amount">
                  {formatAmount(opData.amount)}
                </div>
              </div>
            </div>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">
                  <span className="label-icon">👤</span>From
                </span>
                <span className="detail-value">
                  <AccountLink account={opData.from} />
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">
                  <span className="label-icon">👤</span>To
                </span>
                <span className="detail-value">
                  <AccountLink account={opData.to} />
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">
                  <span className="label-icon">💰</span>Amount
                </span>
                <span className="detail-value amount-highlight">
                  {formatAmount(opData.amount)}
                </span>
              </div>
              {opData.memo && (
                <div className="detail-item memo-item">
                  <span className="detail-label">
                    <span className="label-icon">📝</span>Memo
                  </span>
                  <span className="detail-value memo-content">
                    {opData.memo}
                  </span>
                </div>
              )}
            </div>
          </div>
        );
      case 'vote':
        return (
          <div className="operation-details">
            <div className="operation-summary">
              <div className="summary-box vote-summary">
                <span className="summary-icon">🗳️</span>
                <div className="summary-content">
                  <div className="summary-title">Vote</div>
                  <div className="summary-description">
                    <AccountLink account={opData.voter} /> voted on post
                  </div>
                </div>
                <div className="summary-weight">
                  {formatVoteWeight(opData.weight)}
                </div>
              </div>
            </div>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">
                  <span className="label-icon">👤</span>Voter
                </span>
                <span className="detail-value">
                  <AccountLink account={opData.voter} />
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">
                  <span className="label-icon">✍️</span>Author
                </span>
                <span className="detail-value">
                  <AccountLink account={opData.author} />
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">
                  <span className="label-icon">🔗</span>Permlink
                </span>
                <span className="detail-value permlink-text">
                  {opData.permlink}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">
                  <span className="label-icon">⚖️</span>Vote Weight
                </span>
                <span className="detail-value">
                  {formatVoteWeight(opData.weight)}
                </span>
              </div>
            </div>
          </div>
        );
      case 'comment':
        return (
          <div className="operation-details">
            <div className="operation-summary">
              <div className="summary-box comment-summary">
                <span className="summary-icon">💬</span>
                <div className="summary-content">
                  <div className="summary-title">
                    {opData.parent_author ? 'Comment' : 'Post'}
                  </div>
                  <div className="summary-description">
                    <AccountLink account={opData.author} /> {opData.parent_author ? 'commented' : 'posted'}
                  </div>
                </div>
                <div className="summary-type">
                  {opData.parent_author ? '💬' : '📝'}
                </div>
              </div>
            </div>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">
                  <span className="label-icon">👤</span>Author
                </span>
                <span className="detail-value">
                  <AccountLink account={opData.author} />
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">
                  <span className="label-icon">🔗</span>Permlink
                </span>
                <span className="detail-value permlink-text">
                  {opData.permlink}
                </span>
              </div>
              {opData.parent_author && (
                <div className="detail-item">
                  <span className="detail-label">
                    <span className="label-icon">👥</span>Parent Author
                  </span>
                  <span className="detail-value">
                    <AccountLink account={opData.parent_author} />
                  </span>
                </div>
              )}
              {opData.title && (
                <div className="detail-item title-item">
                  <span className="detail-label">
                    <span className="label-icon">📋</span>Title
                  </span>
                  <span className="detail-value title-content">
                    {opData.title}
                  </span>
                </div>
              )}
              {opData.body && (
                <div className="detail-item body-item">
                  <span className="detail-label">
                    <span className="label-icon">📄</span>Content
                  </span>
                  <span className="detail-value body-content">
                    {opData.body.length > 200 ? (
                      <details className="content-expandable">
                        <summary className="content-preview">
                          {opData.body.substring(0, 200)}... <span className="expand-text">Show more</span>
                        </summary>
                        <div className="content-full">{opData.body}</div>
                      </details>
                    ) : (
                      opData.body
                    )}
                  </span>
                </div>
              )}
            </div>
          </div>
        );
      case 'transfer_to_vesting':
        return (
          <div className="operation-details">
            <div className="operation-summary">
              <div className="summary-box vesting-summary">
                <span className="summary-icon">💎</span>
                <div className="summary-content">
                  <div className="summary-title">Power Up</div>
                  <div className="summary-description">
                    Converting STEEM to Steem Power
                  </div>
                </div>
                <div className="summary-amount">
                  {formatAmount(opData.amount)}
                </div>
              </div>
            </div>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">
                  <span className="label-icon">👤</span>From
                </span>
                <span className="detail-value">
                  <AccountLink account={opData.from} />
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">
                  <span className="label-icon">👤</span>To
                </span>
                <span className="detail-value">
                  <AccountLink account={opData.to} />
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">
                  <span className="label-icon">💰</span>Amount
                </span>
                <span className="detail-value amount-highlight">
                  {formatAmount(opData.amount)}
                </span>
              </div>
            </div>
          </div>
        );
      case 'withdraw_vesting':
        return (
          <div className="operation-details">
            <div className="operation-summary">
              <div className="summary-box powerdown-summary">
                <span className="summary-icon">📤</span>
                <div className="summary-content">
                  <div className="summary-title">Power Down</div>
                  <div className="summary-description">
                    Starting withdrawal process
                  </div>
                </div>
                <div className="summary-amount">
                  {formatAmount(opData.vesting_shares)}
                </div>
              </div>
            </div>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">
                  <span className="label-icon">👤</span>Account
                </span>
                <span className="detail-value">
                  <AccountLink account={opData.account} />
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">
                  <span className="label-icon">💎</span>Vesting Shares
                </span>
                <span className="detail-value amount-highlight">
                  {formatAmount(opData.vesting_shares)}
                </span>
              </div>
            </div>
          </div>
        );
      case 'delegate_vesting_shares':
        return (
          <div className="operation-details">
            <div className="operation-summary">
              <div className="summary-box delegation-summary">
                <span className="summary-icon">🤝</span>
                <div className="summary-content">
                  <div className="summary-title">Delegation</div>
                  <div className="summary-description">
                    <AccountLink account={opData.delegator} /> → <AccountLink account={opData.delegatee} />
                  </div>
                </div>
                <div className="summary-amount">
                  {formatAmount(opData.vesting_shares)}
                </div>
              </div>
            </div>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">
                  <span className="label-icon">👤</span>Delegator
                </span>
                <span className="detail-value">
                  <AccountLink account={opData.delegator} />
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">
                  <span className="label-icon">👤</span>Delegatee
                </span>
                <span className="detail-value">
                  <AccountLink account={opData.delegatee} />
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">
                  <span className="label-icon">💎</span>Vesting Shares
                </span>
                <span className="detail-value amount-highlight">
                  {formatAmount(opData.vesting_shares)}
                </span>
              </div>
            </div>
          </div>
        );
      case 'custom_json':
        return (
          <div className="operation-details">
            <div className="operation-summary">
              <div className="summary-box custom-summary">
                <span className="summary-icon">🔧</span>
                <div className="summary-content">
                  <div className="summary-title">Custom JSON</div>
                  <div className="summary-description">
                    ID: {opData.id || 'Unknown'}
                  </div>
                </div>
                <div className="summary-type">
                  📋
                </div>
              </div>
            </div>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">
                  <span className="label-icon">🏷️</span>ID
                </span>
                <span className="detail-value">
                  {opData.id || 'N/A'}
                </span>
              </div>
              {opData.required_auths && opData.required_auths.length > 0 && (
                <div className="detail-item">
                  <span className="detail-label">
                    <span className="label-icon">🔑</span>Required Auths
                  </span>
                  <span className="detail-value">
                    {opData.required_auths.map((auth, index) => (
                      <span key={index} className="auth-item">
                        <AccountLink account={auth} />
                      </span>
                    ))}
                  </span>
                </div>
              )}
              {opData.required_posting_auths && opData.required_posting_auths.length > 0 && (
                <div className="detail-item">
                  <span className="detail-label">
                    <span className="label-icon">✏️</span>Posting Auths
                  </span>
                  <span className="detail-value">
                    {opData.required_posting_auths.map((auth, index) => (
                      <span key={index} className="auth-item">
                        <AccountLink account={auth} />
                      </span>
                    ))}
                  </span>
                </div>
              )}
              {opData.json && (
                <div className="detail-item json-item">
                  <span className="detail-label">
                    <span className="label-icon">📄</span>JSON Data
                  </span>
                  <span className="detail-value">
                    <details className="json-expandable">
                      <summary className="json-summary">
                        View JSON Data <span className="expand-text">↓</span>
                      </summary>
                      <div className="json-content">
                        <pre>{JSON.stringify(JSON.parse(opData.json || '{}'), null, 2)}</pre>
                      </div>
                    </details>
                  </span>
                </div>
              )}
            </div>
          </div>
        );
      default:
        return (
          <div className="operation-details">
            <div className="operation-summary">
              <div className="summary-box default-summary">
                <span className="summary-icon">{getOperationIcon(opType)}</span>
                <div className="summary-content">
                  <div className="summary-title">{opType.replace(/_/g, ' ').toUpperCase()}</div>
                  <div className="summary-description">
                    Blockchain operation
                  </div>
                </div>
                <div className="summary-type">
                  📄
                </div>
              </div>
            </div>
            <div className="detail-grid">
              <div className="detail-item json-item">
                <span className="detail-label">
                  <span className="label-icon">📄</span>Raw Data
                </span>
                <span className="detail-value">
                  <details className="json-expandable">
                    <summary className="json-summary">
                      View Operation Data <span className="expand-text">↓</span>
                    </summary>
                    <div className="json-content">
                      <pre>{JSON.stringify(opData, null, 2)}</pre>
                    </div>
                  </details>
                </span>
              </div>
            </div>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="transaction-result-page">
        <div className="container">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading transaction details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="transaction-result-page">
        <div className="container">
          <div className="error-state">
            <h1>Transaction Not Found</h1>
            <p className="error-message">{error}</p>
            <p className="transaction-id-error">Transaction ID: <code>{transactionId}</code></p>
            <div className="error-note">
              <p>Please check that the transaction ID is correct and try again.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="transaction-result-page">
      <div className="container">
        {/* Header Section */}
        <div className="page-header">
          <h1 className="page-title">Transaction Details</h1>
          <p className="page-subtitle">Detailed information about Steem blockchain transaction</p>
        </div>

        {/* Search Section */}
        <section className="transaction-search-section">
          <div className="transaction-search-container">
            <form onSubmit={handleSearch} className="transaction-search-wrapper">
              <input
                type="text"
                placeholder="Search blocks, transactions, or accounts..."
                className="transaction-search-input"
                value={searchTerm}
                onChange={handleInputChange}
              />
              <button type="submit" className="transaction-search-button">
                <svg className="transaction-search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
            </form>
          </div>
        </section>

        {/* Transaction Information Card */}
        <div className="transaction-card">
          <div className="card-header">
            <h2 className="card-title">Transaction Information</h2>
          </div>
          <div className="card-content">
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Transaction ID</span>
                <span className="info-value transaction-id">{transaction.transaction_id}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Block Number</span>
                <span className="info-value">{transaction.block_num ? transaction.block_num : 'N/A'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Transaction Number</span>
                <span className="info-value">{transaction.transaction_num !== undefined ? transaction.transaction_num : 'N/A'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Expiration</span>
                <span className="info-value">{formatDate(transaction.expiration)}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Operations</span>
                <span className="info-value">{transaction.operations ? transaction.operations.length : 0} operation(s)</span>
              </div>
              <div className="info-item">
                <span className="info-label">Signatures</span>
                <span className="info-value">{transaction.signatures ? transaction.signatures.length : 0} signature(s)</span>
              </div>
            </div>
          </div>
        </div>

        {/* Operations Section */}
        {transaction.operations && transaction.operations.length > 0 && (
          <div className="operations-section">
            <div className="section-header">
              <h2 className="section-title">Operations</h2>
            </div>
            <div className="operations-list">
              {transaction.operations.map((operation, index) => (
                <div key={index} className="operation-card">
                  <div className="operation-header" style={{'--op-color': getOperationColor(operation[0])}}>
                    <div className="operation-meta">
                      <span className="operation-index">#{index + 1}</span>
                      <span className="operation-icon">{getOperationIcon(operation[0])}</span>
                    </div>
                    <span className="operation-type" style={{backgroundColor: `${getOperationColor(operation[0])}20`, borderColor: `${getOperationColor(operation[0])}40`, color: getOperationColor(operation[0])}}>
                      {operation[0].replace(/_/g, ' ').toUpperCase()}
                    </span>
                  </div>
                  <div className="operation-content">
                    {renderOperationDetails(operation)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Signatures Section */}
        {transaction.signatures && transaction.signatures.length > 0 && (
          <div className="signatures-section">
            <div className="section-header">
              <h2 className="section-title">Signatures</h2>
            </div>
            <div className="signatures-card">
              <div className="signatures-list">
                {transaction.signatures.map((signature, index) => (
                  <div key={index} className="signature-item">
                    <span className="signature-index">#{index + 1}</span>
                    <span className="signature-value">{signature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionResult;